import React from "react";
import clsx from "clsx";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";
import { useFocus } from "./FocusContext";

const backgroundColors: Record<string, string> = {
    default: "#FFFFFF",
    correct: "#AD6BFF",
    incorrect: "#D9D9D9",
    close: "#FAB85F",
};

const animationVariants: any = {
    idle: {
        rotateX: 0, // Start with no vertical rotation
    },
    flip: ({ delay, backgroundColor }: { delay: number; backgroundColor: string }) => ({
        rotateX: 360, // Rotate vertically halfway to create the "flip" effect
        backgroundColor,
        transition: {
            duration: 0.5,
            ease: [0.4, 0.0, 0.2, 1], // Wordle-like easing
            delay, // Apply the delay dynamically
        },
    }),
    strobe: {
        scale: [1, 1.15, 1],
        transition: "spring",
    },
};

interface TileProps {
    mode?: string;
    value: string;
    onClick: (e: React.MouseEvent) => void;
    animationDelay: number;
    onAnimationComplete: (animationName: string) => void;
    columnIndex: number;
    isFocused: boolean;
    rowIndex: number;
    puzzleTooltipData: any;
    setPuzzleTooltipData: (data: any) => void;
}

const Tile = ({
    mode = "default",
    value,
    onClick,
    animationDelay,
    onAnimationComplete,
    columnIndex,
    isFocused,
    rowIndex,
    puzzleTooltipData,
    setPuzzleTooltipData,
}: TileProps) => {
    const { setFocusedIndex } = useFocus();
    const animateFlip = mode !== "default" ? "flip" : !isEmpty(value) ? "strobe" : "idle";
    const customProperties = {
        delay: animationDelay,
        backgroundColor: backgroundColors[mode],
    };

    const handleClick = (e: React.MouseEvent) => {
        onClick(e);
        setFocusedIndex(columnIndex);
        if (["close", "correct"].includes(mode)) {
            setPuzzleTooltipData({
                ...puzzleTooltipData,
                [`${mode}`]: {
                    columnIndex,
                    rowIndex,
                    columnValue: value,
                    hidden: false,
                },
            });
        }
    };

    return (
        <motion.div
            initial="idle"
            className={clsx(
                "tw-flex tw-h-[30px] tw-w-[30px] tw-justify-center tw-items-center tw-text-2xl tw-font-medium tw-rounded tw-border tw-bg-[#FFFFFF]",
                {
                    "tw-border-[#F69929] tw-border-[2.5px] tw-relative": isFocused,
                    "tw-border-[#2E2E2E]": !isFocused,
                },
            )}
            variants={animationVariants}
            custom={customProperties}
            animate={animateFlip}
            onClick={handleClick}
            onAnimationComplete={onAnimationComplete}
            data-tooltip-id={`tile-tooltip-${rowIndex}-${columnIndex}`}
        >
            {value ?? ""}
            {isFocused && (
                <span className="tw-absolute tw-bottom-1 tw-left-1 tw-right-1 tw-h-[1px] tw-bg-black tw-animate-blink"></span>
            )}
        </motion.div>
    );
};

export default Tile;
